import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Calendar,
  Check,
  CreditCard,
  Pencil,
  RotateCcw,
  Settings,
  Trash2,
} from 'lucide-react';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { Spinner } from '@atoms/Spinner';
import { Modal } from '@atoms/Modal';
import { reminderService } from '@services/reminderService';
import { formatShortDate } from '@utils/formatters';
import { REMINDER_FREQUENCIES, reminderUrgencyLabel } from '@utils/reminder.utils';
import type { NotificationTestResults, Reminder } from '@/types';

function parseHours(hours?: string): string[] {
  if (!hours?.trim()) return ['09:00'];
  return hours.split(',').map((h) => h.trim()).filter(Boolean);
}

function joinHours(hours: string[]): string {
  return hours.filter(Boolean).join(',');
}

function formatNotificationFeedback(
  testResults?: NotificationTestResults,
  context: 'save' | 'custom' = 'save',
): { tone: 'success' | 'warning' | 'error'; lines: string[] } {
  const lines: string[] = [];

  if (context === 'save') {
    lines.push('Bildirim ayarları kaydedildi.');
  } else {
    lines.push('Özel bildirim ayarları kaydedildi.');
    lines.push('Zamanlanmış bildirimler Türkiye saati (Europe/Istanbul) ile gönderilir.');
  }

  if (!testResults) {
    if (context === 'save') {
      lines.push('Test için telefon veya e-posta girin.');
    }
    return { tone: 'success', lines };
  }

  if (testResults.whatsapp.attempted) {
    lines.push(
      testResults.whatsapp.sent
        ? 'WhatsApp test mesajı gönderildi.'
        : `WhatsApp test mesajı gönderilemedi: ${testResults.whatsapp.errorMessage ?? 'bilinmeyen hata'}`,
    );
  }

  if (testResults.email.attempted) {
    lines.push(
      testResults.email.sent
        ? 'E-posta test mesajı gönderildi.'
        : `E-posta test mesajı gönderilemedi: ${testResults.email.errorMessage ?? 'bilinmeyen hata'}`,
    );
  }

  if (context === 'save' && !testResults.whatsapp.attempted && !testResults.email.attempted) {
    lines.push('Test için telefon veya e-posta girin.');
  }

  const hasError =
    (testResults.whatsapp.attempted && !testResults.whatsapp.sent) ||
    (testResults.email.attempted && !testResults.email.sent);
  const hasSuccess =
    (testResults.whatsapp.attempted && testResults.whatsapp.sent) ||
    (testResults.email.attempted && testResults.email.sent);

  if (hasError && hasSuccess) return { tone: 'warning', lines };
  if (hasError) return { tone: 'error', lines };
  return { tone: 'success', lines };
}

function urgencyBadgeClass(urgency?: Reminder['urgency']): string {
  switch (urgency) {
    case 'overdue':
      return 'bg-red-100 text-red-800';
    case 'today':
      return 'bg-brand-100 text-brand-800';
    case 'soon':
      return 'bg-sky-100 text-sky-800';
    default:
      return 'bg-emerald-100 text-emerald-800';
  }
}

export function RemindersSection() {
  const queryClient = useQueryClient();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState<Reminder | null>(null);
  const [editReminder, setEditReminder] = useState<Reminder | null>(null);

  const [customForm, setCustomForm] = useState({
    title: '',
    date: new Date().toISOString().slice(0, 10),
    frequency: 'Tek',
  });
  const [cardForm, setCardForm] = useState({
    title: '',
    statementDay: '15',
    cardType: 'Bireysel',
  });
  const [settingsForm, setSettingsForm] = useState({
    email: '',
    phone: '',
    daysBefore: '3',
    timesPerDay: '1',
    hours: ['09:00'] as string[],
  });
  const [customSettingsForm, setCustomSettingsForm] = useState({
    daysBefore: '3',
    hours: ['09:00'] as string[],
  });
  const [editForm, setEditForm] = useState({
    title: '',
    date: '',
    frequency: 'Tek',
    statementDay: '15',
    cardType: 'Bireysel',
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [notificationFeedback, setNotificationFeedback] = useState<{
    tone: 'success' | 'warning' | 'error';
    lines: string[];
  } | null>(null);

  const { data: reminders, isLoading } = useQuery({
    queryKey: ['reminders'],
    queryFn: () => reminderService.getReminders(),
  });

  const { data: settings } = useQuery({
    queryKey: ['reminders', 'settings'],
    queryFn: () => reminderService.getSettings(),
  });

  useEffect(() => {
    if (!settings) return;
    setSettingsForm({
      email: settings.email ?? '',
      phone: settings.phone ?? '',
      daysBefore: String(settings.daysBefore ?? 3),
      timesPerDay: String(settings.timesPerDay ?? 1),
      hours: parseHours(settings.hours),
    });
  }, [settings]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['reminders'] });
  };

  const createCustomMutation = useMutation({
    mutationFn: () =>
      reminderService.createReminder({
        title: customForm.title,
        type: 'Ozel',
        date: customForm.date,
        frequency: customForm.frequency,
      }),
    onSuccess: () => {
      invalidate();
      setCustomForm({ title: '', date: new Date().toISOString().slice(0, 10), frequency: 'Tek' });
    },
  });

  const createCardMutation = useMutation({
    mutationFn: () =>
      reminderService.createReminder({
        title: cardForm.title,
        type: 'KrediKarti',
        statementDay: Number(cardForm.statementDay),
        cardType: cardForm.cardType,
        frequency: 'Aylik',
      }),
    onSuccess: () => {
      invalidate();
      setCardForm({ title: '', statementDay: '15', cardType: 'Bireysel' });
    },
  });

  const settingsMutation = useMutation({
    mutationFn: () =>
      reminderService.saveSettings({
        email: settingsForm.email || undefined,
        phone: settingsForm.phone || undefined,
        daysBefore: Number(settingsForm.daysBefore),
        timesPerDay: Number(settingsForm.timesPerDay),
        hours: joinHours(settingsForm.hours),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['reminders', 'settings'] });
      invalidate();
      setSettingsOpen(false);
      setNotificationFeedback(formatNotificationFeedback(data.testResults, 'save'));
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => reminderService.completeReminder(id),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => reminderService.deleteReminder(id),
    onSuccess: invalidate,
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: () => reminderService.bulkDelete(selectedIds),
    onSuccess: () => {
      invalidate();
      setSelectedIds([]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      reminderService.updateReminder(editReminder!.id, {
        title: editForm.title,
        date: editReminder!.type === 'Ozel' ? editForm.date : undefined,
        frequency: editReminder!.type === 'Ozel' ? editForm.frequency : undefined,
        statementDay: editReminder!.type === 'KrediKarti' ? Number(editForm.statementDay) : undefined,
        cardType: editReminder!.type === 'KrediKarti' ? editForm.cardType : undefined,
      }),
    onSuccess: () => {
      invalidate();
      setEditReminder(null);
    },
  });

  const customSettingsMutation = useMutation({
    mutationFn: () =>
      reminderService.updateReminder(customOpen!.id, {
        customNotification: 1,
        customDaysBefore: Number(customSettingsForm.daysBefore),
        customHours: joinHours(customSettingsForm.hours),
      }),
    onSuccess: () => {
      invalidate();
      setCustomOpen(null);
      setNotificationFeedback(
        formatNotificationFeedback(undefined, 'custom'),
      );
    },
  });

  const resetCustomMutation = useMutation({
    mutationFn: (id: string) => reminderService.resetCustomSetting(id),
    onSuccess: invalidate,
  });

  const activeReminders = useMemo(
    () => (reminders ?? []).filter((r) => r.status === 'Aktif'),
    [reminders],
  );

  const openEdit = (r: Reminder) => {
    setEditReminder(r);
    setEditForm({
      title: r.title,
      date: r.date?.slice(0, 10) ?? '',
      frequency: r.frequency ?? 'Tek',
      statementDay: String(r.statementDay ?? 15),
      cardType: r.cardType ?? 'Bireysel',
    });
  };

  const openCustomSettings = (r: Reminder) => {
    setCustomOpen(r);
    setCustomSettingsForm({
      daysBefore: String(r.customDaysBefore ?? r.notifyDaysBefore ?? 3),
      hours: parseHours(r.customHours ?? r.notifyHours),
    });
  };

  const updateTimesPerDay = (count: number, target: 'settings' | 'custom') => {
    const n = Math.min(5, Math.max(1, count));
    if (target === 'settings') {
      setSettingsForm((prev) => {
        const hours = [...prev.hours];
        while (hours.length < n) hours.push('09:00');
        while (hours.length > n) hours.pop();
        return { ...prev, hours, timesPerDay: String(n) };
      });
    } else {
      setCustomSettingsForm((prev) => {
        const hours = [...prev.hours];
        while (hours.length < n) hours.push('09:00');
        while (hours.length > n) hours.pop();
        return { ...prev, hours };
      });
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Hatırlatıcı</h1>
          <p className="mt-1 text-sm text-slate-500">
            WhatsApp ve e-posta bildirimleri — zamanlanmış gönderim Türkiye saati ile (Europe/Istanbul)
          </p>
        </div>
        <Button variant="outline" onClick={() => setSettingsOpen(true)}>
          <Settings className="mr-2 h-4 w-4" />
          Bildirim Ayarları
        </Button>
      </div>

      {notificationFeedback && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            notificationFeedback.tone === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : notificationFeedback.tone === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-red-200 bg-red-50 text-red-900'
          }`}
        >
          {notificationFeedback.lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
          <button
            type="button"
            className="mt-2 text-xs font-medium underline opacity-80"
            onClick={() => setNotificationFeedback(null)}
          >
            Kapat
          </button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="card-elevated border-sky-100">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Genel Hatırlatma</h2>
              <p className="text-xs text-slate-500">Toplantı, fatura, sözleşme vb.</p>
            </div>
          </div>
          <div className="space-y-3">
            <Input
              label="Hatırlatma adı"
              placeholder="Örn: FATURA ÖDEMESİ"
              value={customForm.title}
              onChange={(e) => setCustomForm({ ...customForm, title: e.target.value.toUpperCase() })}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Vade tarihi"
                type="date"
                value={customForm.date}
                onChange={(e) => setCustomForm({ ...customForm, date: e.target.value })}
              />
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Sıklık</label>
                <select
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={customForm.frequency}
                  onChange={(e) => setCustomForm({ ...customForm, frequency: e.target.value })}
                >
                  {REMINDER_FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <Button
              className="w-full"
              loading={createCustomMutation.isPending}
              disabled={!customForm.title || !customForm.date}
              onClick={() => createCustomMutation.mutate()}
            >
              Hatırlatma Ekle
            </Button>
          </div>
        </Card>

        <Card className="card-elevated border-red-100">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 text-red-700">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Kredi Kartı Takibi</h2>
              <p className="text-xs text-slate-500">Ekstre gününden son ödeme tarihini otomatik hesaplar</p>
            </div>
          </div>
          <div className="space-y-3">
            <Input
              label="Kart adı"
              placeholder="Örn: MAXIMUM, AXESS"
              value={cardForm.title}
              onChange={(e) => setCardForm({ ...cardForm, title: e.target.value.toUpperCase() })}
            />
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Kart tipi</label>
              <div className="flex gap-2">
                {(['Bireysel', 'Ticari'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setCardForm({ ...cardForm, cardType: type })}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      cardForm.cardType === type
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {type === 'Bireysel' ? 'Bireysel (10 gün)' : 'Ticari (5 gün)'}
                  </button>
                ))}
              </div>
            </div>
            <Input
              label="Hesap kesim günü (1-31)"
              type="number"
              min={1}
              max={31}
              value={cardForm.statementDay}
              onChange={(e) => setCardForm({ ...cardForm, statementDay: e.target.value })}
            />
            <Button
              className="w-full"
              variant="danger"
              loading={createCardMutation.isPending}
              disabled={!cardForm.title || !cardForm.statementDay}
              onClick={() => createCardMutation.mutate()}
            >
              Hesapla ve Ekle
            </Button>
          </div>
        </Card>
      </div>

      <Card className="card-elevated">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-slate-900">Aktif Kayıtlar</h2>
            <p className="text-sm text-slate-500">{activeReminders.length} kayıt</p>
          </div>
          {selectedIds.length > 0 && (
            <Button variant="danger" size="sm" loading={bulkDeleteMutation.isPending} onClick={() => bulkDeleteMutation.mutate()}>
              Seçilenleri Sil ({selectedIds.length})
            </Button>
          )}
        </div>

        {!activeReminders.length ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center text-slate-500">
            <Bell className="mx-auto mb-3 h-10 w-10 opacity-30" />
            <p>Henüz aktif hatırlatma yok.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {activeReminders.map((r) => (
              <li
                key={r.id}
                className="flex flex-col gap-3 rounded-xl border border-slate-100 bg-slate-50/50 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(r.id)}
                    onChange={() =>
                      setSelectedIds((prev) =>
                        prev.includes(r.id) ? prev.filter((x) => x !== r.id) : [...prev, r.id],
                      )
                    }
                    className="mt-1"
                  />
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                      r.type === 'KrediKarti' ? 'bg-red-100 text-red-600' : 'bg-sky-100 text-sky-600'
                    }`}
                  >
                    {r.type === 'KrediKarti' ? <CreditCard className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900">{r.title}</p>
                    <div className="mt-1 flex flex-wrap gap-2">
                      <span className="rounded-md bg-white px-2 py-0.5 text-xs text-slate-600">
                        {r.frequencyLabel ?? r.frequency}
                      </span>
                      {r.type === 'KrediKarti' && r.cardType && (
                        <span className="rounded-md bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                          {r.cardType}
                        </span>
                      )}
                      {r.customNotification ? (
                        <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs text-amber-800">Özel bildirim</span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 sm:justify-end">
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-800">{formatShortDate(r.date)}</p>
                    <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${urgencyBadgeClass(r.urgency)}`}>
                      {reminderUrgencyLabel(r.date)}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" title="Tamamla" onClick={() => completeMutation.mutate(r.id)}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" title="Özel bildirim" onClick={() => openCustomSettings(r)}>
                      <Settings className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" title="Düzenle" onClick={() => openEdit(r)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {r.customNotification ? (
                      <Button size="sm" variant="outline" title="Özel ayarı sıfırla" onClick={() => resetCustomMutation.mutate(r.id)}>
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    ) : null}
                    <Button size="sm" variant="danger" title="Sil" onClick={() => deleteMutation.mutate(r.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal open={settingsOpen} onClose={() => setSettingsOpen(false)} title="Bildirim Ayarları">
        <SettingsFormBody
          form={settingsForm}
          onChange={setSettingsForm}
          onTimesChange={(n) => updateTimesPerDay(n, 'settings')}
          onSave={() => settingsMutation.mutate()}
          loading={settingsMutation.isPending}
        />
      </Modal>

      <Modal open={Boolean(customOpen)} onClose={() => setCustomOpen(null)} title={`Özel Bildirim — ${customOpen?.title ?? ''}`}>
        <SettingsFormBody
          form={{ email: '', phone: '', daysBefore: customSettingsForm.daysBefore, timesPerDay: String(customSettingsForm.hours.length), hours: customSettingsForm.hours }}
          onChange={(f) => setCustomSettingsForm({ daysBefore: f.daysBefore, hours: f.hours })}
          onTimesChange={(n) => updateTimesPerDay(n, 'custom')}
          onSave={() => customSettingsMutation.mutate()}
          loading={customSettingsMutation.isPending}
          compact
        />
      </Modal>

      <Modal open={Boolean(editReminder)} onClose={() => setEditReminder(null)} title="Hatırlatıcı Düzenle">
        <div className="space-y-3">
          <Input label="Başlık" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value.toUpperCase() })} />
          {editReminder?.type === 'Ozel' ? (
            <>
              <Input label="Vade Tarihi" type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
              <div>
                <label className="mb-1 block text-sm font-medium">Sıklık</label>
                <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={editForm.frequency} onChange={(e) => setEditForm({ ...editForm, frequency: e.target.value })}>
                  {REMINDER_FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              <Input label="Ekstre Günü" type="number" min={1} max={31} value={editForm.statementDay} onChange={(e) => setEditForm({ ...editForm, statementDay: e.target.value })} />
              <div>
                <label className="mb-1 block text-sm font-medium">Kart Tipi</label>
                <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={editForm.cardType} onChange={(e) => setEditForm({ ...editForm, cardType: e.target.value })}>
                  <option value="Bireysel">Bireysel</option>
                  <option value="Ticari">Ticari</option>
                </select>
              </div>
            </>
          )}
          <Button loading={updateMutation.isPending} onClick={() => updateMutation.mutate()}>Kaydet</Button>
        </div>
      </Modal>
    </div>
  );
}

function SettingsFormBody({
  form,
  onChange,
  onTimesChange,
  onSave,
  loading,
  compact = false,
}: {
  form: { email: string; phone: string; daysBefore: string; timesPerDay: string; hours: string[] };
  onChange: (f: { email: string; phone: string; daysBefore: string; timesPerDay: string; hours: string[] }) => void;
  onTimesChange: (count: number) => void;
  onSave: () => void;
  loading: boolean;
  compact?: boolean;
}) {
  return (
    <div className="space-y-3">
      {!compact && (
        <>
          <Input label="E-posta" type="email" value={form.email} onChange={(e) => onChange({ ...form, email: e.target.value })} />
          <Input
            label="Telefon (WhatsApp)"
            placeholder="905... veya 316..."
            value={form.phone}
            onChange={(e) => onChange({ ...form, phone: e.target.value })}
          />
          <p className="text-xs text-slate-500">
            Zamanlanmış bildirimler ayarladığınız saatlerde Türkiye saati ile gönderilir. Kayıt sonrası test mesajı gönderilir.
          </p>
        </>
      )}
      {compact && (
        <p className="text-xs text-slate-500">
          WhatsApp ve e-posta adresi genel bildirim ayarlarından kullanılır. Saatler Türkiye saati (Europe/Istanbul) ile uygulanır.
        </p>
      )}
      <Input
        label="Kaç gün önce bildir"
        type="number"
        min={1}
        max={30}
        value={form.daysBefore}
        onChange={(e) => onChange({ ...form, daysBefore: e.target.value })}
      />
      <Input
        label="Günde kaç kez"
        type="number"
        min={1}
        max={5}
        value={form.timesPerDay}
        onChange={(e) => onTimesChange(Number(e.target.value))}
      />
      <div className="space-y-2">
        <p className="text-sm font-medium text-slate-700">Bildirim saatleri</p>
        {form.hours.map((hour, i) => (
          <Input
            key={i}
            label={`Saat ${i + 1}`}
            type="time"
            value={hour}
            onChange={(e) => {
              const hours = [...form.hours];
              hours[i] = e.target.value;
              onChange({ ...form, hours });
            }}
          />
        ))}
      </div>
      <Button loading={loading} onClick={onSave}>
        {compact ? 'Özel Ayarları Kaydet' : 'Kaydet ve Test Mesajı Gönder'}
      </Button>
    </div>
  );
}
